import React, { useCallback } from 'react';

import {
  Form,
  Labeled,
  SaveButton,
  useNotify,
} from 'react-admin';

import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CreateIcon from '@mui/icons-material/Create';

import useEditableField from '../../hooks/useEditableField';
import useHiddenUntilHover from '../../hooks/useHiddenUntilHover';

import errorNotification from '../../utilities/errorNotification';

const EditFieldLayout = ({
  source,
  label,
  showEdit,
  inputElement,
  fieldElement,
}: {
  source: string;
  label: string | false;
  showEdit: boolean;
  inputElement: React.ReactNode;
  fieldElement: React.ReactElement;
}) => {
  const {
    editable,
    updateEditable,
    defaultValues,
    onSuccess,
  } = useEditableField({ source });

  const {
    wrapperSx,
    hiddenElementId,
  } = useHiddenUntilHover();

  const notify = useNotify();

  const onError = useCallback(
    (e: unknown) => notify(errorNotification(e), { type: 'error' }),
    [notify],
  );

  if (!editable) {
    return (
      <Labeled label={label} fullWidth sx={wrapperSx}>
        {showEdit ? (
          <Button
            size="small"
            onClick={updateEditable}
            sx={{ paddingX: 0 }}
            color="inherit"
          >
            <Box
              display="flex"
              gap={2}
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              width="100%"
            >
              {fieldElement}
              <CreateIcon color="secondary" id={hiddenElementId} fontSize="small" />
            </Box>
          </Button>
        ) : fieldElement}
      </Labeled>
    );
  }

  return (
    <Form record={defaultValues}>
      <Labeled label={label} fullWidth>
        <Box
          display="flex"
          flexDirection="column"
          flexWrap="wrap"
          gap={2}
        >
          {inputElement}
          <Box display="flex" gap={2} flexWrap="wrap" justifyContent="flex-end">
            <Button size="small" onClick={updateEditable} variant="outlined">
              Cancel
            </Button>
            <SaveButton
              icon={null}
              size="small"
              variant="contained"
              mutationOptions={{ onSuccess, onError }}
              type="button"
            />
          </Box>
        </Box>
      </Labeled>
    </Form>
  );
};

export default EditFieldLayout;
